import { useState, useEffect } from 'react';
import { SunIcon } from '../icons/SunIcon';
import { MoonIcon } from '../icons/MoonIcon';
import { CrossIcon } from '../icons/CrossIcon';
import { ManuIcon } from '../icons/ManuIcon';
import { GithubIcon, InstagramIcons, LinkedinIcons, SnapchatIcons } from '../icons/NavBarIcons';
import { motion } from 'framer-motion';
import LogoIcon from '../icons/LogoIcon';

const NavBar = ({ toggle, setToggle }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [mode, setMode] = useState('light');

    // Apply theme mode based on user selection
    useEffect(() => {
        const savedMode = localStorage.getItem('theme') || 'light';
        setMode(savedMode);
        document.documentElement.classList.toggle('dark', savedMode === 'dark');

        // Change window background color
        if (savedMode === 'dark') {
            document.body.classList.add('bg-dark');
            document.body.classList.remove('bg-light');
        } else {
            document.body.classList.add('bg-light');
            document.body.classList.remove('bg-dark');
        }
    }, []);

    const handleModeToggle = () => {
        const newMode = mode === 'light' ? 'dark' : 'light';
        setMode(newMode);
        document.documentElement.classList.toggle('dark', newMode === 'dark');
        localStorage.setItem('theme', newMode);
        setToggle(!toggle);

        if (newMode === 'dark') {
            document.body.classList.add('bg-dark');
            document.body.classList.remove('bg-light');
        } else {
            document.body.classList.add('bg-light');
            document.body.classList.remove('bg-dark');
        }
    };

    const handleScroll = (id) => {
        const section = document.getElementById(id);

        if (section) {
            section.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    };

    return (
        <nav className="fixed top-0 left-0 w-full z-40 bg-light dark:bg-dark bg-opacity-70 dark:bg-opacity-70 shadow-md transition-colors duration-300">
            <div className="container mx-auto px-6 pt-4 flex justify-between items-center">
                {/* Logo Section */}
                <a href="/" className="text-xl font-bold text-dark dark:text-light">
                    <LogoIcon className="w-16 h-16 lg:w-20 lg:h-20" />
                </a>

                {/* Mobile menu button */}
                <div className="lg:hidden">
                    <button
                        onClick={() => setIsOpen(!isOpen)}
                        className="text-dark dark:text-light focus:outline-none"
                        aria-label="toggle menu"
                    >
                        {!isOpen ? <ManuIcon /> : ""}
                    </button>
                </div>

                {/* Full screen overlay mobile menu */}
                {isOpen && (
                    <motion.div
                        className="fixed inset-0 z-40 bg-transparent flex justify-center items-center transition-all duration-300 ease-in-out"
                        initial={{ opacity: 0, y: -20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                    >
                        <div className="relative bg-light dark:bg-dark bg-opacity-90 shadow-2xl dark:shadow-light p-8 rounded-2xl w-3/4 h-3/4 text-center flex flex-col items-center justify-center">
                            {/* Close Button */}
                            <button
                                className="absolute top-4 left-4 text-dark dark:text-light"
                                aria-label="Close menu"
                                onClick={() => setIsOpen(false)}
                            >
                                <CrossIcon />
                            </button>

                            {/* Mobile Menu Links */}
                            <div className="flex flex-col space-y-6 mb-8">
                                {[
                                    { path: '#home', label: 'Home' },
                                    { path: '#about', label: 'About' },
                                    { path: '#skills', label: 'Skills' },
                                    { path: '#projects', label: 'Projects' },
                                    { path: '#contact', label: "Contact" }
                                ].map(({ path, label }) => (
                                    <motion.a
                                        key={path}
                                        href={path}
                                        className="text-dark dark:text-light text-xl font-medium transition-colors"
                                        onClick={() => setIsOpen(false)}
                                        whileHover={{ scale: 1.1 }}
                                    >
                                        {label}
                                    </motion.a>
                                ))}
                            </div>

                            {/* Social Icons for Mobile Menu */}
                            <div className="flex space-x-6 items-center">
                                {[
                                    { path: 'https://github.com/Animatedguess', icon: <GithubIcon className="w-8 h-8" />, label: 'GitHub' },
                                    { path: 'https://www.instagram.com/animated_guess/', icon: <InstagramIcons className="w-8 h-8" />, label: 'Instagram' },
                                    { path: 'https://www.linkedin.com/in/kailash-agarwal-657823235/', icon: <LinkedinIcons className="w-8 h-8" />, label: 'LinkedIn' },
                                    { path: 'https://www.snapchat.com/add/k_agr2022', icon: <SnapchatIcons className="w-8 h-8" />, label: 'Snapchat' }
                                ].map(({ path, icon, label }) => (
                                    <motion.a
                                        key={path}
                                        href={path}
                                        target="_blank"
                                        className="text-dark dark:text-light"
                                        whileHover={{ scale: 1.2 }}
                                        aria-label={label}
                                    >
                                        {icon}
                                    </motion.a>
                                ))}

                                {/* Mode Toggle Button with Increased Size */}
                                <motion.button
                                    onClick={handleModeToggle}
                                    className="text-dark dark:text-light w-8 h-8" // Set button size
                                    whileHover={{ rotate: 360 }}
                                >
                                    {mode === 'dark' ? (
                                        <SunIcon className="fill-light w-8 h-8" /> // Set icon size
                                    ) : (
                                        <MoonIcon className="fill-light w-8 h-8" /> // Set icon size
                                    )}
                                </motion.button>
                            </div>
                        </div>
                    </motion.div>
                )}

                {/* Desktop Menu */}
                <div className="hidden lg:flex space-x-10 items-center">
                    {['home', 'about', 'skills', 'projects', 'contact'].map((id) => (
                        <motion.a
                            key={id}
                            href={`#${id}`}
                            className="relative text-lg font-medium text-dark dark:text-light transition-transform transform hover:scale-105 group"
                            onClick={() => handleScroll(id)}
                        >
                            {id.charAt(0).toUpperCase() + id.slice(1)}
                            {/* Underline with Animation */}
                            <span className="absolute left-0 -bottom-1 w-full h-0.5 bg-dark dark:bg-light scale-x-0 group-hover:scale-x-100 transition-transform origin-left duration-300 ease-in-out"></span>
                        </motion.a>
                    ))}
                    <div className="flex space-x-6 items-center">
                        {[
                            { icon: <GithubIcon />, label: 'GitHub', url: 'https://github.com/Animatedguess' },
                            { icon: <InstagramIcons />, label: 'Instagram', url: 'https://www.instagram.com/animated_guess/' },
                            { icon: <LinkedinIcons />, label: 'LinkedIn', url: 'https://www.linkedin.com/in/kailash-agarwal-657823235/' },
                            { icon: <SnapchatIcons />, label: 'Snapchat', url: 'https://www.snapchat.com/add/k_agr2022' }
                        ].map(({ icon, label, url }) => (
                            <motion.div key={label} whileHover={{ scale: 1.2 }} aria-label={label}>
                                <a href={url} target="_blank" rel="noopener noreferrer">
                                    {icon}
                                </a>
                            </motion.div>
                        ))}
                        <motion.button
                            onClick={handleModeToggle}
                            className="text-dark dark:text-light"
                            whileHover={{ rotate: 360 }}
                        >
                            {mode === 'dark' ? (
                                <SunIcon className="fill-light" />
                            ) : (
                                <MoonIcon className="fill-light" />
                            )}
                        </motion.button>
                    </div>
                </div>
            </div>
        </nav>
    );
};

export default NavBar;
